const GENERAL_QUOTE_VALID_DAYS = 15;
export const GENERAL_QUOTE_IMAGE_TTL_DAYS = 90;
export const CURTAIN_QUOTE_TYPE = "curtains";
export const GENERAL_QUOTE_TYPE = "general";

const ONES = [
  "",
  "One",
  "Two",
  "Three",
  "Four",
  "Five",
  "Six",
  "Seven",
  "Eight",
  "Nine",
  "Ten",
  "Eleven",
  "Twelve",
  "Thirteen",
  "Fourteen",
  "Fifteen",
  "Sixteen",
  "Seventeen",
  "Eighteen",
  "Nineteen",
];

const TENS = [
  "",
  "",
  "Twenty",
  "Thirty",
  "Forty",
  "Fifty",
  "Sixty",
  "Seventy",
  "Eighty",
  "Ninety",
];

const toNumber = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
};

const hasOwn = (object, key) =>
  Object.prototype.hasOwnProperty.call(object || {}, key);

const isDevelopmentMode = () =>
  typeof process !== "undefined" && process.env?.NODE_ENV === "development";

export const normalizeGeneralDiscountType = (value) => {
  const raw = String(value ?? "").trim().toLowerCase();

  if (raw === "%" || raw === "percent" || raw === "percentage") {
    return "percent";
  }

  if (
    raw === "" ||
    raw === "flat" ||
    raw === "amount" ||
    raw === "fixed" ||
    raw === "rs" ||
    raw === "inr" ||
    raw === "rupee" ||
    raw === "₹"
  ) {
    return "flat";
  }

  return "flat";
};

export const normalizeGeneralDiscount = (quote = {}, source = "general_quote") => {
  const discountObject =
    quote?.discount && typeof quote.discount === "object" ? quote.discount : null;

  let explicitType = null;
  if (hasOwn(quote, "discountType")) {
    explicitType = normalizeGeneralDiscountType(quote.discountType);
  } else if (hasOwn(quote, "discountMode")) {
    explicitType = normalizeGeneralDiscountType(quote.discountMode);
  } else if (hasOwn(quote, "summaryDiscountMode")) {
    explicitType = quote.summaryDiscountMode === "%" ? "percent" : "flat";
  } else if (discountObject && hasOwn(discountObject, "type")) {
    explicitType = normalizeGeneralDiscountType(discountObject.type);
  } else if (hasOwn(quote, "discountAmount")) {
    explicitType = "flat";
  } else if (hasOwn(quote, "discountPercent")) {
    explicitType = "percent";
  }

  let valueSource = null;
  let rawValue = 0;

  if (hasOwn(quote, "discountValue")) {
    rawValue = quote.discountValue;
    valueSource = "discountValue";
  } else if (discountObject && hasOwn(discountObject, "value")) {
    rawValue = discountObject.value;
    valueSource = "discount.value";
  } else if (hasOwn(quote, "summaryDiscountValue")) {
    rawValue = quote.summaryDiscountValue;
    valueSource = "summaryDiscountValue";
  } else if (explicitType === "percent" && hasOwn(quote, "discountPercent")) {
    rawValue = quote.discountPercent;
    valueSource = "discountPercent";
  } else if (explicitType === "flat" && hasOwn(quote, "discountAmount")) {
    rawValue = quote.discountAmount;
    valueSource = "discountAmount";
  } else if (hasOwn(quote, "discount")) {
    rawValue = discountObject ? discountObject.value : quote.discount;
    valueSource = "discount";
  } else if (hasOwn(quote, "discountAmount")) {
    rawValue = quote.discountAmount;
    valueSource = "discountAmount";
  } else if (hasOwn(quote, "discountPercent")) {
    rawValue = quote.discountPercent;
    valueSource = "discountPercent";
  }

  const numericValue = Math.max(0, toNumber(rawValue));
  let discountType = explicitType;

  if (!discountType) {
    discountType = valueSource ? (numericValue > 100 ? "flat" : "percent") : "flat";
    if (isDevelopmentMode() && valueSource && numericValue > 0) {
      console.warn("Legacy ambiguous discount inferred", {
        source,
        valueSource,
        rawValue,
        discountType,
      });
    }
  }

  const discountValue =
    discountType === "percent" ? Math.min(numericValue, 100) : numericValue;

  return {
    discountType,
    discountValue,
  };
};

export const createClientId = (prefix = "id") => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return `${prefix}-${crypto.randomUUID()}`;
  }

  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

export const getTodayLocalDateString = () => {
  const now = new Date();
  const yyyy = now.getFullYear();
  const mm = String(now.getMonth() + 1).padStart(2, "0");
  const dd = String(now.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

export const dateToLocalDateString = (dateValue) => {
  if (!(dateValue instanceof Date) || Number.isNaN(dateValue.getTime())) {
    return "";
  }

  const yyyy = dateValue.getFullYear();
  const mm = String(dateValue.getMonth() + 1).padStart(2, "0");
  const dd = String(dateValue.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

export const normalizeQuoteDate = (value, fallbackDate = null) => {
  if (value == null || value === "") {
    return fallbackDate ? normalizeQuoteDate(fallbackDate, null) : "";
  }

  if (typeof value?.toDate === "function") {
    return dateToLocalDateString(value.toDate());
  }

  if (value instanceof Date) {
    return dateToLocalDateString(value);
  }

  const raw = String(value || "").trim();
  if (!raw) return fallbackDate ? normalizeQuoteDate(fallbackDate, null) : "";

  const htmlDateMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (htmlDateMatch) {
    const [, year, month, day] = htmlDateMatch;
    const parsed = new Date(Number(year), Number(month) - 1, Number(day), 12, 0, 0, 0);
    if (!Number.isNaN(parsed.getTime())) {
      return `${year}-${month}-${day}`;
    }
  }

  const slashDateMatch = raw.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
  if (slashDateMatch) {
    const [, day, month, year] = slashDateMatch;
    const parsed = new Date(Number(year), Number(month) - 1, Number(day), 12, 0, 0, 0);
    if (!Number.isNaN(parsed.getTime())) {
      return dateToLocalDateString(parsed);
    }
  }

  const parsed = new Date(raw);
  if (!Number.isNaN(parsed.getTime())) {
    return dateToLocalDateString(parsed);
  }

  return fallbackDate ? normalizeQuoteDate(fallbackDate, null) : "";
};

export const resolveQuoteDateForInput = (quote = {}, fallbackDate = null) =>
  normalizeQuoteDate(
    quote?.quoteDate ||
      quote?.date ||
      quote?.quotationDate ||
      quote?.generalQuote?.quoteDate ||
      quote?.generalQuoteData?.quoteDate ||
      quote?.createdAt ||
      quote?.createdOn ||
      quote?.createdDate ||
      "",
    fallbackDate
  );

export const resolveQuoteDateForDisplay = (quote = {}, fallbackDate = getTodayLocalDateString()) => {
  const normalized = resolveQuoteDateForInput(quote, fallbackDate);
  if (!normalized) return "";

  const parsed = normalizeQuoteDate(normalized);
  if (!parsed) return normalized;

  const [year, month, day] = parsed.split("-").map((part) => Number(part));
  const dateValue = new Date(year, month - 1, day, 12, 0, 0, 0);
  if (Number.isNaN(dateValue.getTime())) return normalized;

  return dateValue.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
};

export const todayISO = getTodayLocalDateString;

export const addDaysISO = (days = GENERAL_QUOTE_VALID_DAYS) => {
  const d = new Date();
  d.setDate(d.getDate() + Number(days || 0));
  return dateToLocalDateString(d);
};

export const buildBusinessDisplayName = (businessName = "", shopAddress = "") => {
  const direct = String(businessName || "").trim();
  if (direct) return direct;

  return "FabriQuote";
};

export const createGeneralQuoteItem = () => ({
  id: createClientId("general-item"),
  name: "",
  itemName: "",
  category: "",
  description: "",
  showDescription: false,
  quantity: "1",
  unit: "pcs",
  rate: "",
  amount: "",
  gstPercent: 0,
  gstMode: "inclusive",
  images: [],
  imageUrl: "",
  imageStoragePath: "",
  imageExpiresAt: "",
  imageUploadedAt: "",
  imageName: "",
  imagePreviewName: "",
  productUrl: "",
  imageLinkUrl: "",
  specTemplateId: null,
  specTemplateName: "",
  specItemType: "",
  isTemplateModified: false,
  isExecutionLocked: false,
  specifications: [],
});

export const createGeneralQuoteSpecification = (specification = {}) => ({
  id:
    String(specification?.id || "").trim() ||
    createClientId("general-spec"),
  key: String(
    specification?.key ?? specification?.label ?? specification?.name ?? ""
  ),
  value:
    specification?.value == null
      ? ""
      : String(specification.value),
  type: String(specification?.type || "text"),
});

export const normalizeGeneralQuoteItemSpecifications = (item = {}) =>
  (Array.isArray(item?.specifications) ? item.specifications : [])
    .map((specification) => createGeneralQuoteSpecification(specification))
    .filter(
      (specification) =>
        String(specification?.key ?? "").trim() ||
        String(specification?.value ?? "").trim()
    );

const FIREBASE_STORAGE_REST_URL_PATTERN =
  /^https?:\/\/firebasestorage\.googleapis\.com\/v0\/b\/([^/]+)\/o\/([^?&#]+)/i;

const isHttpUrl = (value = "") => /^https?:\/\//i.test(String(value || "").trim());

const isGsUrl = (value = "") => /^gs:\/\//i.test(String(value || "").trim());

const safeDecodeURIComponent = (value = "") => {
  try {
    return decodeURIComponent(String(value || ""));
  } catch {
    return String(value || "");
  }
};

const normalizeStorageFullPath = (value = "") => {
  const raw = String(value || "").trim();
  if (!raw) return "";
  if (isHttpUrl(raw)) return extractFirebaseStorageFullPathFromUrl(raw);
  if (isGsUrl(raw)) {
    const withoutScheme = raw.replace(/^gs:\/\//i, "");
    const slashIndex = withoutScheme.indexOf("/");
    return slashIndex >= 0 ? withoutScheme.slice(slashIndex + 1) : "";
  }
  return raw.replace(/^\/+/, "");
};

export const isFirebaseStorageRestUrl = (value = "") =>
  FIREBASE_STORAGE_REST_URL_PATTERN.test(String(value || "").trim());

export const hasFirebaseStorageDownloadToken = (value = "") => {
  const url = String(value || "").trim();
  if (!url) return false;
  try {
    const parsed = new URL(url);
    return Boolean(parsed.searchParams.get("token"));
  } catch {
    return /[?&]token=/i.test(url);
  }
};

export const isProtectedFirebaseStorageRestUrl = (value = "") =>
  isFirebaseStorageRestUrl(value) && !hasFirebaseStorageDownloadToken(value);

export const extractFirebaseStorageFullPathFromUrl = (value = "") => {
  const url = String(value || "").trim();
  if (!url) return "";
  const match = url.match(FIREBASE_STORAGE_REST_URL_PATTERN);
  if (match?.[2]) {
    return safeDecodeURIComponent(match[2]).replace(/^\/+/, "");
  }
  if (isGsUrl(url)) {
    return normalizeStorageFullPath(url);
  }
  return "";
};

export const extractFirebaseStorageBucketFromUrl = (value = "") => {
  const url = String(value || "").trim();
  if (!url) return "";
  const restMatch = url.match(FIREBASE_STORAGE_REST_URL_PATTERN);
  if (restMatch?.[1]) return restMatch[1];
  if (isGsUrl(url)) {
    const withoutScheme = url.replace(/^gs:\/\//i, "");
    return withoutScheme.split("/")[0] || "";
  }
  return "";
};

const firstString = (...values) =>
  values.map((value) => String(value || "").trim()).find(Boolean) || "";

const isImageLikeAttachment = (image = {}) => {
  if (!image || typeof image !== "object") return false;
  const type = String(
    image.type || image.contentType || image.mimeType || image.fileType || ""
  ).toLowerCase();
  const name = String(
    image.filename || image.name || image.fileName || image.imageName || ""
  ).toLowerCase();
  const url = firstString(
    image.downloadURL,
    image.downloadUrl,
    image.imageUrl,
    image.imageURL,
    image.url,
    image.previewUrl,
    image.src,
    image.fileUrl,
    image.publicUrl,
    image.fullPath,
    image.storagePath,
    image.path
  ).toLowerCase();
  return (
    type.startsWith("image/") ||
    /\.(png|jpe?g|webp|gif|bmp|avif)$/i.test(name) ||
    /\.(png|jpe?g|webp|gif|bmp|avif)(\?|#|$)/i.test(url)
  );
};

export const normalizeGeneralQuoteImage = (rawImage = {}) => {
  const image =
    typeof rawImage === "string"
      ? isHttpUrl(rawImage) || isGsUrl(rawImage)
        ? { url: rawImage, storagePath: rawImage }
        : { storagePath: rawImage }
      : rawImage && typeof rawImage === "object"
      ? rawImage
      : {};

  const rawUrl = firstString(
    image.downloadURL,
    image.downloadUrl,
    image.download_url,
    image.imageUrl,
    image.imageURL,
    image.url,
    image.previewUrl,
    image.previewURL,
    image.src,
    image.photoUrl,
    image.photoURL,
    image.fileUrl,
    image.publicUrl,
    image.legacyUrl
  );
  const rawPath = firstString(
    image.fullPath,
    image.storageFullPath,
    image.storagePath,
    image.path,
    image.filePath,
    image.imageStoragePath,
    image.gsUrl,
    image.gsURL,
    image.legacyPath,
    typeof rawImage === "string" && !isHttpUrl(rawImage) ? rawImage : ""
  );
  const extractedPath = extractFirebaseStorageFullPathFromUrl(rawUrl);
  const fullPath = normalizeStorageFullPath(rawPath || extractedPath);
  const storageBucket =
    firstString(image.storageBucket, image.bucket) ||
    extractFirebaseStorageBucketFromUrl(rawUrl || rawPath);
  const protectedStorageUrl = isProtectedFirebaseStorageRestUrl(rawUrl);
  const usableDisplayUrl = rawUrl && !protectedStorageUrl ? rawUrl : "";
  const downloadURL = firstString(
    image.downloadURL,
    image.downloadUrl,
    image.download_url,
    usableDisplayUrl
  );
  const fileName = firstString(
    image.filename,
    image.fileName,
    image.name,
    image.imagePreviewName,
    image.imageName,
    image.originalName
  );
  const idSeed = firstString(image.id, image.imageId, fullPath, downloadURL, rawUrl, fileName);
  const sizeValue = Number(image.sizeBytes ?? image.size ?? image.fileSize);
  const contentType = firstString(
    image.contentType,
    image.type,
    image.mimeType,
    image.fileType
  );

  return {
    id: idSeed || createClientId("general-image"),
    imageId: firstString(image.imageId, image.id, idSeed),
    fileId: firstString(image.fileId),
    name: fileName,
    filename: fileName,
    originalFilename: firstString(image.originalFilename, image.originalName, fileName),
    safeFilename: firstString(image.safeFilename, fileName),
    contentType,
    sizeBytes: Number.isFinite(sizeValue) ? sizeValue : null,
    originalSizeBytes: Number.isFinite(Number(image.originalSizeBytes))
      ? Number(image.originalSizeBytes)
      : null,
    optimizedSizeBytes: Number.isFinite(Number(image.optimizedSizeBytes))
      ? Number(image.optimizedSizeBytes)
      : null,
    thumbnailSizeBytes: Number.isFinite(Number(image.thumbnailSizeBytes))
      ? Number(image.thumbnailSizeBytes)
      : null,
    compressionRatio: Number.isFinite(Number(image.compressionRatio))
      ? Number(image.compressionRatio)
      : null,
    width: Number.isFinite(Number(image.width)) ? Number(image.width) : null,
    height: Number.isFinite(Number(image.height)) ? Number(image.height) : null,
    originalWidth: Number.isFinite(Number(image.originalWidth))
      ? Number(image.originalWidth)
      : null,
    originalHeight: Number.isFinite(Number(image.originalHeight))
      ? Number(image.originalHeight)
      : null,
    fullPath,
    thumbnailFullPath: normalizeStorageFullPath(
      firstString(image.thumbnailFullPath, image.thumbnailStoragePath)
    ),
    downloadURL: downloadURL && !isProtectedFirebaseStorageRestUrl(downloadURL) ? downloadURL : "",
    thumbnailDownloadURL: firstString(
      image.thumbnailDownloadURL,
      image.thumbnailUrl,
      image.thumbnailURL
    ),
    bucket: storageBucket,
    storageBucket,
    uploadStatus: firstString(image.uploadStatus),
    compressionStatus: firstString(image.compressionStatus),
    thumbnailStatus: firstString(image.thumbnailStatus),
    uploadedBy: firstString(image.uploadedBy, image.uploadedByUid),
    ownerType: firstString(image.ownerType),
    ownerId: firstString(image.ownerId),
    workspaceUid: firstString(image.workspaceUid),
    quoteId: firstString(image.quoteId),
    itemId: firstString(image.itemId),
    rowId: firstString(image.rowId),
    leadId: firstString(image.leadId),
    removedFromRecord: image.removedFromRecord === true,
    archived: image.archived === true,
    uploadedAt: firstString(image.uploadedAt, image.imageUploadedAt, image.createdAt),
    updatedAt: firstString(image.updatedAt),
    expiresAt: firstString(image.expiresAt, image.imageExpiresAt),
    legacyUrl: isFirebaseStorageRestUrl(rawUrl) ? rawUrl : firstString(image.legacyUrl),
    legacyPath: firstString(image.legacyPath, rawPath && rawPath !== fullPath ? rawPath : ""),
    legacyFullPath: firstString(image.legacyFullPath),
    legacyMissingImage: image.legacyMissingImage || null,
    replacedMissingImage: image.replacedMissingImage === true,
    repairedFromMissing: image.repairedFromMissing === true,
    replacedAt: firstString(image.replacedAt),
    repairedFromPath: firstString(image.repairedFromPath),
    repairMethod: firstString(image.repairMethod),
    repairedAt: firstString(image.repairedAt),
    repairedBy: firstString(image.repairedBy),
    removedFromQuote: image.removedFromQuote === true,
    removedAt: firstString(image.removedAt),
    removedBy: firstString(image.removedBy),
    removalReason: firstString(image.removalReason),
    cleanupEligibleAt: firstString(image.cleanupEligibleAt),
    cleanupRetentionDays: Number.isFinite(Number(image.cleanupRetentionDays))
      ? Number(image.cleanupRetentionDays)
      : null,
    cleanupStatus: firstString(image.cleanupStatus),
    removedFromQuoteId: firstString(image.removedFromQuoteId),
    removedFromQuoteNo: firstString(image.removedFromQuoteNo),
    removedFromItemId: firstString(image.removedFromItemId),
    removedFromItemName: firstString(image.removedFromItemName),
    url: downloadURL && !isProtectedFirebaseStorageRestUrl(downloadURL) ? downloadURL : "",
    previewUrl:
      firstString(image.previewUrl, image.previewURL, image.thumbnailDownloadURL, downloadURL) &&
      !isProtectedFirebaseStorageRestUrl(
        firstString(image.previewUrl, image.previewURL, image.thumbnailDownloadURL, downloadURL)
      )
        ? firstString(image.previewUrl, image.previewURL, image.thumbnailDownloadURL, downloadURL)
        : "",
    storagePath: fullPath,
    path: fullPath,
    size: Number.isFinite(sizeValue) ? sizeValue : null,
    type: contentType,
    resolveStatus: image.resolveStatus || "",
    resolveErrorCode: image.resolveErrorCode || "",
    resolveErrorMessage: image.resolveErrorMessage || "",
  };
};

export const createGeneralQuoteImage = normalizeGeneralQuoteImage;

export const normalizeGeneralQuoteItemImages = (item = {}) => {
  const rawImages = [];
  [
    item?.images,
    item?.itemImages,
    item?.productImages,
    item?.referenceImages,
    item?.attachments,
  ].forEach((candidate) => {
    if (!Array.isArray(candidate)) return;
    candidate.forEach((image) => {
      if (
        typeof image === "string" ||
        isImageLikeAttachment(image) ||
        firstString(image?.fullPath, image?.storagePath, image?.path, image?.imageStoragePath)
      ) {
        rawImages.push(image);
      }
    });
  });
  [item?.image, item?.imageData, item?.referenceImage, item?.productImage].forEach(
    (candidate) => {
      if (typeof candidate === "string" || isImageLikeAttachment(candidate)) {
        rawImages.push(candidate);
      }
    }
  );
  const hasLegacyImage =
    String(item?.imageUrl || "").trim() ||
    String(item?.imageURL || "").trim() ||
    String(item?.downloadURL || item?.downloadUrl || "").trim() ||
    String(item?.imageStoragePath || "").trim() ||
    String(item?.fullPath || item?.storagePath || item?.path || item?.filePath || "").trim() ||
    String(item?.gsUrl || item?.gsURL || "").trim() ||
    String(item?.imageName || item?.imagePreviewName || item?.filename || "").trim();

  if (hasLegacyImage) {
    rawImages.push({
      id: item?.imageId || createClientId("general-image"),
      downloadURL: item?.downloadURL || item?.downloadUrl || "",
      url: item?.imageUrl || item?.imageURL || "",
      previewUrl: item?.previewUrl || item?.imageUrl || item?.imageURL || "",
      fullPath: item?.fullPath || "",
      storagePath:
        item?.imageStoragePath ||
        item?.storagePath ||
        item?.path ||
        item?.filePath ||
        item?.gsUrl ||
        item?.gsURL ||
        "",
      filename: item?.imagePreviewName || item?.imageName || item?.filename || item?.name || "",
      uploadedAt: item?.imageUploadedAt || item?.uploadedAt || "",
      expiresAt: item?.imageExpiresAt || item?.expiresAt || "",
      contentType: item?.imageContentType || item?.contentType || item?.type || "",
      sizeBytes: item?.imageSizeBytes || item?.sizeBytes || item?.size || null,
    });
  }

  const seen = new Set();

  return rawImages
    .map((image) => normalizeGeneralQuoteImage(image))
    .filter(
      (image) =>
        image.url ||
        image.previewUrl ||
        image.downloadURL ||
        image.fullPath ||
        image.storagePath ||
        image.filename
    )
    .filter((image) => {
      const key =
        image.fullPath ||
        image.downloadURL ||
        image.url ||
        image.legacyUrl ||
        image.filename ||
        image.id;
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
};

export const syncGeneralQuoteItemPrimaryImageFields = (item = {}) => {
  const images = normalizeGeneralQuoteItemImages(item);
  const primary = images[0] || null;

  return {
    ...item,
    images,
    imageUrl: primary?.downloadURL || primary?.url || "",
    imageStoragePath: primary?.fullPath || primary?.storagePath || "",
    imageExpiresAt: primary?.expiresAt || "",
    imageUploadedAt: primary?.uploadedAt || "",
    imageName: primary?.filename || "",
    imagePreviewName: primary?.filename || "",
  };
};

export const createDefaultGeneralQuote = ({
  shopAddress = "",
  currency = "Rs. Indian Rupee",
  businessName,
  businessPhone = "",
  businessEmail = "",
} = {}) => ({
  title: "Quotation",
  subtitle: "",
  quoteNo: "",
  quoteDate: todayISO(),
  validTill: addDaysISO(),
  currency,
  quoteFrom: {
    name: buildBusinessDisplayName(businessName, shopAddress),
    address: shopAddress || "",
    phone: businessPhone || "",
    email: businessEmail || "",
  },
  quoteFor: {
    name: "",
    company: "",
    address: "",
    phone: "",
    email: "",
  },
  lineItems: [createGeneralQuoteItem()],
  discountType: "flat",
  discountValue: "0",
  discountApplication: "after_gst",
  additionalChargesLabel: "Additional Charges",
  additionalChargesValue: "0",
  additionalChargeGstPercent: 0,
  additionalChargeGstMode: "exclusive",
  notes: "",
  terms:
    "Payment due as per agreed schedule. Delivery timelines start after approval and advance receipt.",
});

export const normalizeGeneralQuote = (
  quote = {},
  {
    shopAddress = "",
    currency = "Rs. Indian Rupee",
    businessName,
    businessPhone = "",
    businessEmail = "",
  } = {}
) => {
  const base = createDefaultGeneralQuote({
    shopAddress,
    currency,
    businessName,
    businessPhone,
    businessEmail,
  });

  const incomingItems = Array.isArray(quote.lineItems)
    ? quote.lineItems
    : Array.isArray(quote.items)
    ? quote.items
    : [];
  const normalizedDiscount = normalizeGeneralDiscount(
    quote,
    "normalize_general_quote"
  );

  const settingsBusinessName = buildBusinessDisplayName(businessName, shopAddress);
  const settingsAddress = String(shopAddress || "").trim();

  const normalizedLineItems =
    incomingItems.length > 0
      ? incomingItems.map((item) =>
          syncGeneralQuoteItemPrimaryImageFields({
            ...createGeneralQuoteItem(),
            ...item,
            id: item?.id || createClientId("general-item"),
            name: item?.itemName || item?.name || "",
            itemName: item?.itemName || item?.name || "",
            category: item?.category || "",
            amount:
              item?.amount == null || item?.amount === ""
                ? ""
                : String(item.amount),
            quantity:
              item?.quantity == null || item?.quantity === ""
                ? "1"
                : String(item.quantity),
            unit: item?.unit || "pcs",
            rate:
              item?.rate == null || item?.rate === ""
                ? ""
                : String(item.rate),
            gstPercent: [0, 5, 12, 18].includes(Number(item?.gstPercent))
              ? Number(item?.gstPercent)
              : 0,
            gstMode:
              item?.gstMode === "exclusive" ? "exclusive" : "inclusive",
            productUrl: item?.imageLinkUrl || item?.productUrl || "",
            imageLinkUrl: item?.imageLinkUrl || item?.productUrl || "",
            specTemplateId:
              item?.specTemplateId == null || item?.specTemplateId === ""
                ? null
                : String(item.specTemplateId),
            specTemplateName: String(item?.specTemplateName ?? ""),
            specItemType: String(item?.specItemType ?? ""),
            isTemplateModified: item?.isTemplateModified === true,
            isExecutionLocked: item?.isExecutionLocked === true,
            specifications: normalizeGeneralQuoteItemSpecifications(item),
          })
        )
      : [createGeneralQuoteItem()];

  return {
    ...base,
    ...quote,
    currency: quote.currency || currency || base.currency,
    quoteNo: quote.quoteNo || "",
    quoteDate: resolveQuoteDateForInput(quote, base.quoteDate),
    date: resolveQuoteDateForInput(quote, base.quoteDate),
    quotationDate: resolveQuoteDateForInput(quote, base.quoteDate),
    validTill: quote.validTill || base.validTill,
    quoteFrom: {
      ...base.quoteFrom,
      ...(quote.quoteFrom || {}),
      name: String(businessName || "").trim()
        ? settingsBusinessName
        : quote.quoteFrom?.name || base.quoteFrom.name,
      address: settingsAddress || quote.quoteFrom?.address || base.quoteFrom.address,
      phone: String(businessPhone || "").trim() || quote.quoteFrom?.phone || base.quoteFrom.phone,
      email: String(businessEmail || "").trim() || quote.quoteFrom?.email || base.quoteFrom.email,
    },
    quoteFor: {
      ...base.quoteFor,
      ...(quote.quoteFor || {}),
    },
    discountType: normalizedDiscount.discountType,
    discountValue: String(normalizedDiscount.discountValue),
    discountApplication: quote.discountApplication || "after_gst",
    additionalChargesValue:
      quote.additionalChargesValue != null
        ? String(quote.additionalChargesValue)
        : quote.additionalCharges != null
        ? String(quote.additionalCharges)
        : base.additionalChargesValue,
    additionalChargeGstPercent: [0, 5, 12, 18].includes(
      Number(quote?.additionalChargeGstPercent)
    )
      ? Number(quote.additionalChargeGstPercent)
      : 0,
    additionalChargeGstMode:
      quote?.additionalChargeGstMode === "inclusive"
        ? "inclusive"
        : "exclusive",
    lineItems: normalizedLineItems,
    items: normalizedLineItems,
  };
};

export const getGeneralItemBaseAmount = (item) => {
  const quantity = toNumber(item?.quantity);
  const rate = toNumber(item?.rate);
  return Number((quantity * rate).toFixed(2));
};

export const getGeneralItemTaxAmount = (item) => {
  const baseAmount = getGeneralItemBaseAmount(item);
  const gstPercent = Math.max(0, toNumber(item?.gstPercent));

  if (gstPercent <= 0) return 0;

  if (item?.gstMode === "exclusive") {
    return Number(((baseAmount * gstPercent) / 100).toFixed(2));
  }

  return Number((baseAmount - baseAmount / (1 + gstPercent / 100)).toFixed(2));
};

export const calcGeneralItemGstAmount = (item) => getGeneralItemTaxAmount(item);

export const getGeneralItemAmount = (item) => {
  const baseAmount = getGeneralItemBaseAmount(item);
  const taxAmount = getGeneralItemTaxAmount(item);

  if (item?.gstMode === "exclusive") {
    return Number((baseAmount + taxAmount).toFixed(2));
  }

  return Number(baseAmount.toFixed(2));
};

export const calcGeneralSubtotal = (items = []) =>
  Number(
    (Array.isArray(items) ? items : []).reduce(
      (sum, item) => sum + getGeneralItemAmount(item),
      0
    ).toFixed(2)
  );

export const calcGeneralDiscount = (
  subtotal,
  discountType = "flat",
  discountValue = 0
) => {
  const baseSubtotal = Math.max(0, toNumber(subtotal));
  const rawDiscount = Math.max(0, toNumber(discountValue));
  const normalizedType = normalizeGeneralDiscountType(discountType);

  if (normalizedType === "percent") {
    const safePercent = Math.min(rawDiscount, 100);
    return Number(((baseSubtotal * safePercent) / 100).toFixed(2));
  }

  return Number(Math.min(rawDiscount, baseSubtotal).toFixed(2));
};

export const calcAdditionalChargeBase = (quote = {}) =>
  Number(
    Math.max(
      0,
      toNumber(
        quote.additionalChargesValue != null
          ? quote.additionalChargesValue
          : quote.additionalCharges
      )
    ).toFixed(2)
  );

export const calcAdditionalChargeGstAmount = (quote = {}) => {
  const base = calcAdditionalChargeBase(quote);
  const percent = Number(quote.additionalChargeGstPercent || 0);
  const mode = quote.additionalChargeGstMode || "exclusive";

  if (!base || !percent) return 0;

  if (mode === "inclusive") {
    return Number((base - base / (1 + percent / 100)).toFixed(2));
  }

  return Number((base * (percent / 100)).toFixed(2));
};

export const calcAdditionalChargeTotal = (quote = {}) => {
  const base = calcAdditionalChargeBase(quote);
  const gst = calcAdditionalChargeGstAmount(quote);
  const mode = quote.additionalChargeGstMode || "exclusive";

  if (mode === "inclusive") return base;
  return Number((base + gst).toFixed(2));
};

export const calcGeneralItemsTotalGst = (items = []) =>
  Number(
    (Array.isArray(items) ? items : []).reduce(
      (sum, item) => sum + calcGeneralItemGstAmount(item),
      0
    ).toFixed(2)
  );

export const resolveGeneralQuoteLineItems = (quote = {}) =>
  Array.isArray(quote.lineItems)
    ? quote.lineItems
    : Array.isArray(quote.items)
    ? quote.items
    : [];

export const calcGeneralTotalGst = (quote = {}) =>
  Number(
    (
      calcGeneralItemsTotalGst(resolveGeneralQuoteLineItems(quote)) +
      calcAdditionalChargeGstAmount(quote)
    ).toFixed(2)
  );

export const calcGeneralGrandTotal = (quote = {}) => {
  const items = resolveGeneralQuoteLineItems(quote);
  const subtotal = calcGeneralSubtotal(items);
  const additionalChargeTotal = calcAdditionalChargeTotal(quote);
  const totalAfterGst = Number((subtotal + additionalChargeTotal).toFixed(2));
  const normalizedDiscount = normalizeGeneralDiscount(
    quote,
    "general_grand_total"
  );
  const discount = calcGeneralDiscount(
    totalAfterGst,
    normalizedDiscount.discountType,
    normalizedDiscount.discountValue
  );

  return Number(
    Math.max(0, totalAfterGst - discount).toFixed(2)
  );
};

const twoDigitWords = (value) => {
  const num = Math.floor(toNumber(value));
  if (num < 20) return ONES[num];
  const ten = Math.floor(num / 10);
  const one = num % 10;
  return `${TENS[ten]}${one ? ` ${ONES[one]}` : ""}`.trim();
};

const threeDigitWords = (value) => {
  const num = Math.floor(toNumber(value));
  if (num === 0) return "";

  const hundred = Math.floor(num / 100);
  const rest = num % 100;

  if (!hundred) return twoDigitWords(rest);

  return `${ONES[hundred]} Hundred${rest ? ` ${twoDigitWords(rest)}` : ""}`.trim();
};

const integerToIndianWords = (value) => {
  let num = Math.floor(Math.abs(toNumber(value)));
  if (!num) return "Zero";

  const parts = [];

  const crore = Math.floor(num / 10000000);
  if (crore) {
    parts.push(`${integerToIndianWords(crore)} Crore`);
    num %= 10000000;
  }

  const lakh = Math.floor(num / 100000);
  if (lakh) {
    parts.push(`${integerToIndianWords(lakh)} Lakh`);
    num %= 100000;
  }

  const thousand = Math.floor(num / 1000);
  if (thousand) {
    parts.push(`${integerToIndianWords(thousand)} Thousand`);
    num %= 1000;
  }

  const hundredChunk = threeDigitWords(num);
  if (hundredChunk) {
    parts.push(hundredChunk);
  }

  return parts.join(" ").trim();
};

const getCurrencyWord = (currency = "") => {
  const cur = String(currency || "").toLowerCase();
  if (cur.includes("rupee") || cur.includes("inr")) return ["Rupee", "Paise"];
  if (cur.includes("dollar") || cur.includes("usd")) return ["Dollar", "Cents"];
  if (cur.includes("dirham") || cur.includes("aed")) return ["Dirham", "Fils"];
  if (cur.includes("riyal")) return ["Riyal", "Halala"];
  if (cur.includes("pound") || cur.includes("gbp")) return ["Pound", "Pence"];
  return ["Unit", "Cents"];
};

export const amountToWords = (amount, currency = "Rs. Indian Rupee") => {
  const value = Math.max(0, toNumber(amount));
  const integer = Math.floor(value);
  const decimal = Math.round((value - integer) * 100);
  const [major, minor] = getCurrencyWord(currency);

  const majorText = `${integerToIndianWords(integer)} ${major}${integer === 1 ? "" : "s"}`;
  const minorText =
    decimal > 0
      ? ` and ${integerToIndianWords(decimal)} ${minor}`
      : "";

  return `${majorText}${minorText} Only`;
};

export const calculateGeneralQuoteTotals = (quote = {}) => {
  const normalizedDiscount = normalizeGeneralDiscount(
    quote,
    "general_quote_totals"
  );
  const items = resolveGeneralQuoteLineItems(quote);
  const lineTotals = items.map((item, index) => {
    const baseAmount = getGeneralItemBaseAmount(item);
    const gstAmount = getGeneralItemTaxAmount(item);
    const lineTotal = getGeneralItemAmount(item);
    const taxableAmount =
      item?.gstMode === "inclusive"
        ? Number(Math.max(0, baseAmount - gstAmount).toFixed(2))
        : baseAmount;

    return {
      index,
      id: item?.id || "",
      taxableAmount,
      baseAmount,
      gstAmount,
      total: lineTotal,
      gstPercent: Number(item?.gstPercent || 0),
      gstMode: item?.gstMode === "exclusive" ? "exclusive" : "inclusive",
    };
  });
  const subtotalExcludingGst = Number(
    lineTotals.reduce((sum, item) => sum + Number(item.taxableAmount || 0), 0).toFixed(2)
  );
  const subtotal = calcGeneralSubtotal(items);
  const itemsGstTotal = calcGeneralItemsTotalGst(items);
  const additionalCharges = calcAdditionalChargeBase(quote);
  const additionalChargesGst = calcAdditionalChargeGstAmount(quote);
  const additionalChargesTotal = calcAdditionalChargeTotal(quote);
  const additionalChargesTaxableAmount =
    quote.additionalChargeGstMode === "inclusive"
      ? Number(Math.max(0, additionalCharges - additionalChargesGst).toFixed(2))
      : additionalCharges;
  const gstTotal = Number((itemsGstTotal + additionalChargesGst).toFixed(2));
  const totalAfterGst = Number((subtotal + additionalChargesTotal).toFixed(2));
  const discountAmount = calcGeneralDiscount(
    totalAfterGst,
    normalizedDiscount.discountType,
    normalizedDiscount.discountValue
  );
  const grandTotal = Number(Math.max(0, totalAfterGst - discountAmount).toFixed(2));

  if (isDevelopmentMode()) {
    console.log("QUOTE DISCOUNT CALC", {
      quoteType: GENERAL_QUOTE_TYPE,
      subtotal,
      totalAfterGst,
      discountType: normalizedDiscount.discountType,
      discountValue: normalizedDiscount.discountValue,
      discountAmount,
      grandTotal,
    });
  }

  return {
    subtotalExcludingGst,
    subtotalBeforeGst: subtotalExcludingGst,
    taxableAmount: Number(
      (subtotalExcludingGst + additionalChargesTaxableAmount).toFixed(2)
    ),
    subtotal,
    itemGstAmount: itemsGstTotal,
    itemsGstTotal,
    gstTotal,
    totalGstAmount: gstTotal,
    totalAfterGst,
    discountApplication: "after_gst",
    discountType: normalizedDiscount.discountType,
    discountValue: normalizedDiscount.discountValue,
    discountAmount,
    additionalCharges,
    additionalChargesTaxableAmount,
    additionalChargesGst,
    additionalChargesAmount: additionalCharges,
    additionalChargesGstAmount: additionalChargesGst,
    additionalChargesTotal,
    grandTotal,
    finalTotal: grandTotal,
    totalInWords: amountToWords(grandTotal, quote.currency),
    lineTotals,
  };
};
